import { getBrandColor } from 'utils/brandColor';

/**
 * Jetons du Nouveau Panier. La peau (typo, champs, boutons) vit dans
 * checkout-v2.css ; ici seulement ce qui dépend du site : couleur de marque,
 * rayon des cartes, et les variables CSS posées sur la racine .CheckoutV2.
 */
export const ink = '#0a0a0a';
export const ink2 = '#1a1a1a';
export const gray700 = '#4a4a4a';
export const gray500 = '#7a7a7a';
export const gray300 = '#d4d4d4';
export const gray200 = '#e8e8e8';
export const gray100 = '#f5f5f5';

export const accent = getBrandColor();
export const radius = `${Math.max(Number(mondayMindbody?.customization?.cardBorderRadius) || 16, 10)}px`;

/** `#rrggbb` + alpha 0..1 → `rgba()` ; repli sur la couleur brute sinon. */
export function withAlpha(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Couleur de texte lisible sur la couleur de marque : blanc sur un rouge
 * Dynamo, noir sur un jaune clair. Luminance relative WCAG.
 */
export function readableOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return L > 0.45 ? ink : '#ffffff';
}

export const accentText = readableOn(accent);

/**
 * Accent utilisable sur fond noir (carte récapitulatif, barre mobile) : une
 * marque noire — ou le repli quand la couleur n'est pas réglée en BO — y
 * serait invisible, on passe alors au blanc.
 */
export const accentOnDark = readableOn(accent) === '#ffffff' && isVeryDark(accent) ? '#ffffff' : accent;

function isVeryDark(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 60;
}

/**
 * Police configurée dans l'admin du plugin (Personalisation → « Police »,
 * ex. Eastman sur Dynamo), la même que le compteur Stretch. Vide → le Panier
 * hérite des polices du site (checkout-v2.css n'impose alors aucune famille).
 */
export const configuredFont = (typeof mondayMindbody !== 'undefined' ? mondayMindbody?.customization?.fontFamily : '')?.trim() || '';

/** Variables CSS consommées par checkout-v2.css. */
export const cssVars = {
  ...(configuredFont ? { '--cv2-font': `'${configuredFont.replace(/'/g, '')}', 'Helvetica Neue', Helvetica, Arial, sans-serif` } : {}),
  '--cv2-accent': accent,
  '--cv2-accent-text': accentText,
  '--cv2-accent-on-dark': accentOnDark,
  '--cv2-accent-on-dark-text': readableOn(accentOnDark),
  '--cv2-accent-soft': withAlpha(accent, 0.09),
  '--cv2-accent-shadow': `0 12px 28px ${withAlpha(accent, 0.3)}`,
  '--cv2-radius': radius,
} as Record<string, string>;
